import { isEqual, isMatch, uniqBy } from "lodash";
import AppContext from "PFApp/app_context";
import { CalendarMode, CalendarView, Row } from "PFApp/booking/types";
import { RowType } from "PFApp/booking/types/row";
import { WorkforceItem } from "PFCore/hooks/queries/bookings/workforce/use_workforce_entries";
import { Filters } from "PFTypes";
import { useContext, useEffect, useRef } from "react";

import { getRowFromDataItem } from "../../../helpers";
import { useBookingModuleContext } from "../../../providers/booking_module_context_provider";
import { WorkforceCalendarData } from "../booking_overview_context.types";
import { useBookingsOverviewContext } from "../bookings_overview_context";
import { BookingRowsContextType } from "./booking_rows_context";

interface UseWorkforceRows {
  setRowsByCalendarMode: BookingRowsContextType["setRowsByCalendarMode"];
  onRowsReplace: VoidFunction;
}

export const useWorkforceRows = ({ setRowsByCalendarMode, onRowsReplace }: UseWorkforceRows): void => {
  const {
    store: { currentProfile }
  } = useContext(AppContext);
  const { calendarRange } = useBookingModuleContext();

  const prevCalendarRange = useRef(calendarRange);
  const prevFilters = useRef<Filters>();
  const prevSortOrder = useRef<string>();
  const prevPage = useRef<number>();

  const {
    calendarMode,
    calendarData: { data, view, page }
  } = useBookingsOverviewContext<WorkforceCalendarData>();

  useEffect(() => {
    if (calendarMode !== CalendarMode.Workforce) {
      return;
    }
    const isFilterChanged = !isEqual(prevFilters.current, data.meta?.filters);
    const isOrderChanged = prevSortOrder.current !== data.meta.order?.value?.text;
    const isCalendarRangeChanged = !isMatch(calendarRange, prevCalendarRange.current);
    const isPageChanged = !isEqual(prevPage.current, page);

    const shouldReplaceRows = isFilterChanged || isOrderChanged || isCalendarRangeChanged || isPageChanged;
    setRowsByCalendarMode((prevState) => {
      const prevRows = prevState[calendarMode];
      const newRows = (data.entries as WorkforceItem[]).reduce((acc, row) => {
        const rowData = getRowFromDataItem(row, currentProfile, CalendarMode.Workforce);
        const isSummaryRow = calendarMode === CalendarMode.Workforce && rowData.type === RowType.Profile;
        if (view === CalendarView.BookingsOnly && !isSummaryRow) {
          return acc;
        }

        return acc.concat(rowData);
      }, [] as Row[]);

      const rows = shouldReplaceRows ? newRows : uniqBy(prevRows.concat(newRows), (row) => row.key);
      return {
        ...prevState,
        [calendarMode]: rows
      };
    });

    if (shouldReplaceRows) {
      onRowsReplace();
    }

    prevSortOrder.current = data.meta.order?.value?.text;
    prevCalendarRange.current = calendarRange;
    prevFilters.current = data?.meta?.filters;
    prevPage.current = page;
  }, [data.entries, view]);
};
